import { GOOGLE_AUTOMATION_STAGE_STATUSES } from './googleAutomationStageStatuses'
import * as googleUrls from './googleUrls'
import { GoogleSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, GoogleRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import SectionNotFoundException from '../exception/sectionNotFoundException'
import { getAttrPromise, wait } from '../interruptiblePromises'
import {
  AutomationErrors,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, getOptionalElement, shouldUpdateSetting } from '../utils'

export const getCurrentValueFn = (key: GoogleSettingKey) =>
  getCurrentValueFnFromKey(key, (value) => (value === 'true' ? 'On' : 'Off'))

const targetValues = {
  [GoogleSettingKey.RESTRICTED_PROFILE]: 'true',
  [GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE]: 'false',
}

export const getIsFulfilledFn = (key: GoogleSettingKey) =>
  getIsFulfilledFnFromKey(key, (value) => value === targetValues[key])

export const getCantUndoFn = () => defaultCantUndoFn

export function getIsDisabledFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) => getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabled
}

export function getIsDisabledReasonFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) =>
    getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabledReason
}

async function getModal(
  settingKey: GoogleSettingKey,
): Promise<HTMLElement | null> {
  if (settingKey === GoogleSettingKey.RESTRICTED_PROFILE) {
    return getOptionalElement(
      document,
      '//div[@aria-label="Profile privacy"]',
      { timeout: 1500 },
    )
  } else if (settingKey === GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE) {
    return getOptionalElement(
      document,
      '//div[@aria-label="Your public profile"]',
      { timeout: 1500 },
    )
  }
  return null
}

export async function openProfileSettings(
  settingKey: GoogleSettingKey,
): Promise<void> {
  const modal = await getModal(settingKey)

  if (!modal) {
    const profileSettingsButton = await Promise.any([
      getElement<HTMLButtonElement>(
        document,
        '//div[@aria-label="Profile settings" and @role="button"]',
      ),
      getElement<HTMLButtonElement>(
        document,
        '//button[@aria-label="Profile settings"]',
      ),
    ])

    profileSettingsButton.click()
    await wait(500)
  }
}

async function getRestrictedProfileToggle(): Promise<HTMLElement | null> {
  const restrictedProfileCheckbox = await getOptionalElement<HTMLButtonElement>(
    document,
    '//button[@role="switch" and @aria-label="Restricted profile"]',
  )

  return restrictedProfileCheckbox
}

async function getShowPostsOnYourPublicProfileToggle(): Promise<HTMLElement | null> {
  const showPostsOnYourPublicProfileCheckbox =
    await getOptionalElement<HTMLButtonElement>(
      document,
      '//button[@role="switch" and @aria-label="Show your posts on your profile"]',
    )

  return showPostsOnYourPublicProfileCheckbox
}

export const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_MAPS_PROFILE_PRIVACY_RESTRICTED_PROFILE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.mapsContributionsUrl.navigateForUser(googleAccountIndex)

      await openProfileSettings(GoogleSettingKey.RESTRICTED_PROFILE)

      const restrictedProfileToggle = await getRestrictedProfileToggle()

      if (!restrictedProfileToggle) {
        await userContext.updateStoredAutomationErrors(
          {
            [GoogleSettingKey.RESTRICTED_PROFILE]: {
              gatherError: {
                message: 'Restricted profile is not available for this account',
                name: SectionNotFoundException.exceptionName,
              },
            },
          },
          GoogleRecommendationKey.GOOGLE_MAPS_PROFILE_PRIVACY,
        )
      } else {
        await userContext.updateStoredInitialValues(
          {
            [GoogleSettingKey.RESTRICTED_PROFILE]:
              restrictedProfileToggle.ariaChecked,
          },
          GoogleRecommendationKey.GOOGLE_MAPS_PROFILE_PRIVACY,
        )
      }
    },
  },
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_MAPS_PROFILE_PRIVACY_SHOW_POSTS_ON_YOUR_PUBLIC_PROFILE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.mapsContributionsUrl.navigateForUser(googleAccountIndex)

      await openProfileSettings(GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE)

      const showPostsOnPublicProfileToggle =
        await getShowPostsOnYourPublicProfileToggle()

      if (!showPostsOnPublicProfileToggle) {
        await userContext.updateStoredAutomationErrors(
          {
            [GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE]: {
              gatherError: {
                message:
                  'Show posts on your public profile is not available for this account',
                name: SectionNotFoundException.exceptionName,
              },
            },
          },
          GoogleRecommendationKey.GOOGLE_MAPS_PROFILE_PRIVACY,
        )
      } else {
        await userContext.updateStoredInitialValues(
          {
            [GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE]:
              showPostsOnPublicProfileToggle.ariaChecked,
          },
          GoogleRecommendationKey.GOOGLE_MAPS_PROFILE_PRIVACY,
        )
      }
    },
  },
]

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_MAPS_PROFILE_PRIVACY_RESTRICTED_PROFILE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.mapsContributionsUrl.navigateForUser(googleAccountIndex)

      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          GoogleSettingKey.RESTRICTED_PROFILE,
          getIsFulfilledFn,
        )
      ) {
        await openProfileSettings(GoogleSettingKey.RESTRICTED_PROFILE)
        const restrictedProfileToggle = await getRestrictedProfileToggle()

        if (restrictedProfileToggle && !isDry) {
          restrictedProfileToggle.click()
          await getAttrPromise(restrictedProfileToggle, 'aria-checked')
        }
      }
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_MAPS_PROFILE_PRIVACY_SHOW_POSTS_ON_YOUR_PUBLIC_PROFILE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.mapsContributionsUrl.navigateForUser(googleAccountIndex)

      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE,
          getIsFulfilledFn,
        )
      ) {
        await openProfileSettings(GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE)
        const showPostsOnPublicProfileToggle =
          await getShowPostsOnYourPublicProfileToggle()

        if (showPostsOnPublicProfileToggle && !isDry) {
          showPostsOnPublicProfileToggle.click()
          await getAttrPromise(showPostsOnPublicProfileToggle, 'aria-checked')
        }
      }
    },
  },
]

export const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_MAPS_PROFILE_PRIVACY_RESTRICTED_PROFILE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.mapsContributionsUrl.navigateForUser(googleAccountIndex)

      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          GoogleSettingKey.RESTRICTED_PROFILE,
          getIsFulfilledFn,
        )
      ) {
        await openProfileSettings(GoogleSettingKey.RESTRICTED_PROFILE)
        const restrictedProfileToggle = await getRestrictedProfileToggle()

        if (
          restrictedProfileToggle &&
          restrictedProfileToggle.ariaChecked ===
            targetValues[GoogleSettingKey.RESTRICTED_PROFILE] &&
          !isDry
        ) {
          restrictedProfileToggle.click()
          await getAttrPromise(restrictedProfileToggle, 'aria-checked')
        }
      }
    },
  },
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_MAPS_PROFILE_PRIVACY_SHOW_POSTS_ON_YOUR_PUBLIC_PROFILE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.mapsContributionsUrl.navigateForUser(googleAccountIndex)

      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE,
          getIsFulfilledFn,
        )
      ) {
        await openProfileSettings(GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE)

        const showPostsOnPublicProfileToggle =
          await getShowPostsOnYourPublicProfileToggle()

        if (
          showPostsOnPublicProfileToggle &&
          showPostsOnPublicProfileToggle.ariaChecked ===
            targetValues[GoogleSettingKey.SHOW_POSTS_PUBLIC_PROFILE] &&
          !isDry
        ) {
          showPostsOnPublicProfileToggle.click()
          await getAttrPromise(showPostsOnPublicProfileToggle, 'aria-checked')
        }
      }
    },
  },
]
